import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { Spinner } from '../spinner';

export type ButtonVariant =
  | 'primary'
  | 'outline'
  | 'secondary'
  | 'dark'
  | 'neutral'
  | 'muted'
  | 'danger'
  | 'ghost'
  | 'ghostAccent'
  | 'quiet'
  | 'ghostInverse'
  | 'report'
  | 'overlay';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: ButtonVariant;
  size: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  /** Square button: the icon goes in `children` or `startIcon`; pass `aria-label`. */
  iconOnly?: boolean;
  /** A toggle that is on: gold whatever the variant, and `aria-pressed`. Leave it out for a plain button. */
  pressed?: boolean;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 min-w-20 px-3 text-label-sm',
  md: 'h-10 min-w-20 px-4 text-label',
  lg: 'h-12 min-w-20 px-6 text-label-lg',
};

const iconOnlySizeClasses: Record<ButtonSize, string> = {
  sm: 'size-8 shrink-0',
  md: 'size-10 shrink-0',
  lg: 'size-12 shrink-0',
};

// Text and icon colour never change on hover. An action drawn as text is `TextButton`.
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'border-primary bg-primary text-white hover:border-primary-hover hover:bg-primary-hover active:border-primary-active active:bg-primary-active',
  outline: 'border-primary bg-transparent text-primary hover:bg-danger-surface active:bg-primary/12',
  // A quiet action beside a form — attach, add a row — that must not compete with the red one.
  secondary: 'border-line-strong bg-transparent text-ink hover:bg-surface-muted active:bg-line',
  dark: 'border-ink bg-ink text-white hover:border-ink-hover hover:bg-ink-hover active:border-ink-active active:bg-ink-active',
  neutral:
    'border-neutral bg-neutral text-ink hover:border-neutral-hover hover:bg-neutral-hover active:border-neutral-active active:bg-neutral-active',
  // An option already taken that reads as settled — a cast vote — yet can still be undone.
  muted:
    'border-ink-muted bg-ink-muted text-white hover:border-ink hover:bg-ink active:border-ink-active active:bg-ink-active',
  danger:
    'border-danger bg-danger text-white hover:border-primary-hover hover:bg-primary-hover active:border-primary-active active:bg-primary-active',
  ghost: 'border-transparent bg-transparent text-ink hover:bg-surface-muted active:bg-line',
  // On a gold panel: the hover is gold too.
  ghostAccent: 'border-transparent bg-transparent text-ink hover:bg-accent-soft active:bg-accent',
  // A secondary icon that only darkens on hover — «•••» on a card.
  quiet: 'border-transparent bg-transparent text-ink-subtle hover:text-ink',
  // The journal's AI reports.
  report: 'border-report-fill bg-report-fill text-white',
  // For dark surfaces.
  ghostInverse: 'border-transparent bg-transparent text-white hover:bg-white/10 active:bg-white/20',
  // Floats over an image or a video, whatever its colours.
  overlay: 'border-transparent bg-black/60 text-white hover:bg-black/70 active:bg-black/80',
};

const pressedClasses = 'border-accent bg-accent text-ink';

// Disabled keeps the variant's own colours, faded — a red button stays red. Its hover and press are dropped.
const disabledClasses = (variant: ButtonVariant) =>
  cx(
    'cursor-not-allowed opacity-50',
    variantClasses[variant].split(' ').filter((c) => !c.startsWith('hover:') && !c.startsWith('active:')),
  );

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant: variantProp,
      size,
      fullWidth,
      loading,
      disabled,
      startIcon,
      endIcon,
      iconOnly,
      pressed,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const variant = variantProp ?? (iconOnly ? 'neutral' : 'primary');
    const isDisabled = disabled || loading;
    const innerRef = useRef<HTMLButtonElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLButtonElement);
    // The button keeps the width it had before loading started, so the swap to a spinner does not shift the layout.
    const [lockedWidth, setLockedWidth] = useState<number>();
    useLayoutEffect(() => {
      setLockedWidth(loading ? innerRef.current?.offsetWidth : undefined);
    }, [loading]);

    if (import.meta.env.DEV && iconOnly && !rest['aria-label']) {
      console.error('Button: `iconOnly` needs an `aria-label`.');
    }

    return (
      <button
        ref={innerRef}
        className={cx(
          'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control border-[1.5px] transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          iconOnly ? iconOnlySizeClasses[size] : sizeClasses[size],
          disabled
            ? disabledClasses(variant)
            : [pressed ? pressedClasses : variantClasses[variant], loading ? 'cursor-progress' : 'cursor-pointer'],
          fullWidth && 'w-full',
          className,
        )}
        style={lockedWidth ? { width: lockedWidth } : undefined}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        aria-pressed={pressed}
        title={iconOnly ? rest['aria-label'] : undefined}
        {...rest}
      >
        {loading ? <Spinner size="sm" label="Загрузка" /> : startIcon}
        {!(loading && iconOnly) && children}
        {endIcon}
      </button>
    );
  },
);

Button.displayName = 'Button';
